import { ArrowUpRight, Users, Building2, Mic, Play, Eye, Hash } from "lucide-react";
import { Link } from "react-router-dom";
import { CornerMarks, StatusLED } from "@/components/infra";

const GROUP_PHOTO =
  "https://customer-assets.emergentagent.com/job_book-minimal-1/artifacts/qlkyqw61_1758652358290.jpeg";
const JSA_PHOTO =
  "https://customer-assets.emergentagent.com/job_book-minimal-1/artifacts/rua29goe_1758652357393.jpeg";

const PROGRAM_URL =
  "https://www.datacloudglobalcongress.com/welcome/talent-tech";
const JSA_VIDEO_ID = "pesI5QRkhOY";
const JSA_VIDEO_URL = `https://www.youtube.com/watch?v=${JSA_VIDEO_ID}`;
const JSA_EMBED_URL = `https://www.youtube-nocookie.com/embed/${JSA_VIDEO_ID}?rel=0&modestbranding=1`;

const HASHTAGS = [
  "TalentInTech",
  "DigitalInfrastructure",
  "DataCloudUSA",
  "DataCloudUSA2025",
];

const COMPANIES = [
  "NVIDIA",
  "Google",
  "Microsoft",
  "Vertiv",
  "Oracle",
  "Project Production Institute",
  "Nomad Futurist",
];

const STATS = [
  { Icon: Users, value: "15", label: "Young professionals" },
  { Icon: Building2, value: "7", label: "Corporate partners" },
  { Icon: Mic, value: "JSA 20", label: "On-camera feature" },
];

export default function TalentInTech() {
  return (
    <section
      id="talent-in-tech"
      data-testid="talent-in-tech-section"
      className="border-b hairline bg-[#F4F5F7] relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-12">
          <div className="col-span-12 lg:col-span-5">
            <div data-testid="talent-in-tech-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / Talent in Tech
            </div>
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance">
              Selected for{" "}
              <span className="text-chrome">Talent in Tech.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <p className="text-base sm:text-lg text-[#3D4049] leading-relaxed max-w-xl">
              At{" "}
              <strong className="text-[#0F1117] font-semibold">
                #DataCloudUSA
              </strong>
              , Luke joined the{" "}
              <strong className="text-[#0F1117] font-semibold">
                #TalentInTech
              </strong>{" "}
              initiative — a hand-picked cohort of 15 young professionals learning
              directly from leaders at NVIDIA, Google, Microsoft, Vertiv, Oracle,
              Project Production Institute, and Nomad Futurist. The program builds
              lasting connections with peers and direct lines to the industry&apos;s
              senior operators.
            </p>
          </div>
        </div>

        {/* Photo grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {/* Group photo */}
          <figure
            data-testid="tit-photo-group"
            className="chrome-surface relative p-2"
          >
            <CornerMarks color="#A9ADB4" />
            <div className="relative">
              <img
                src={GROUP_PHOTO}
                alt="DCYB at DataCloud USA — Talent in Tech cohort"
                className="w-full h-[360px] md:h-[420px] object-cover object-center"
                draggable="false"
              />
              <div className="absolute bottom-0 inset-x-0 bg-white/90 backdrop-blur-sm border-t hairline px-4 py-2.5 flex items-center justify-between">
                <span className="font-mono-label text-[10px] text-[#5F6168]">
                  REF · TIT-COHORT
                </span>
                <span className="font-mono-label text-[10px] text-[#0F1117]">
                  DataCloud USA · GE Vernova
                </span>
              </div>
            </div>
            <figcaption className="px-2 pt-3 pb-1 font-mono-label text-[10px] text-[#71747B]">
              / 15 emerging leaders, one cohort.
            </figcaption>
          </figure>

          {/* JSA photo */}
          <figure
            data-testid="tit-photo-jsa"
            className="chrome-surface relative p-2"
          >
            <CornerMarks color="#A9ADB4" />
            <div className="relative">
              <img
                src={JSA_PHOTO}
                alt="Luke on JSA TV with Dean Perrine — 20 Years of Marketing Innovation"
                className="w-full h-[360px] md:h-[420px] object-cover object-center"
                draggable="false"
              />
              <div className="absolute bottom-0 inset-x-0 bg-white/90 backdrop-blur-sm border-t hairline px-4 py-2.5 flex items-center justify-between">
                <span className="font-mono-label text-[10px] text-[#5F6168]">
                  REF · JSA-INTERVIEW
                </span>
                <span className="font-mono-label text-[10px] text-[#0F1117] flex items-center gap-1.5">
                  <StatusLED color="#10B981" />
                  Aired
                </span>
              </div>
            </div>
            <figcaption className="px-2 pt-3 pb-1 font-mono-label text-[10px] text-[#71747B]">
              / On JSA TV with Dean Perrine — 20 Years of Marketing Innovation.
            </figcaption>
          </figure>
        </div>

        {/* JSA TV interview — featured video */}
        <div
          data-testid="tit-jsa-interview"
          className="chrome-surface relative grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#D2D5DA] border hairline mb-12 overflow-hidden"
        >
          <CornerMarks color="#A9ADB4" />
          {/* Video */}
          <div className="lg:col-span-7 bg-black relative" style={{ aspectRatio: "16 / 9" }}>
            <iframe
              src={JSA_EMBED_URL}
              title="JSA TV · Talent in Tech Spotlight · Luke Adams · DPGlobal Assets"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          {/* Meta */}
          <div className="lg:col-span-5 bg-white p-7 md:p-8 flex flex-col">
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono-label text-[10px] text-[#71747B] flex items-center gap-1.5">
                <Play size={11} className="text-[#0F1117]" />
                / JSA TV · TALENT IN TECH SPOTLIGHT
              </span>
              <span className="font-mono-label text-[10px] text-[#0F1117] flex items-center gap-1.5">
                <StatusLED color="#10B981" />
                Live
              </span>
            </div>

            <h3 className="font-display text-xl md:text-2xl font-extrabold text-[#0F1117] tracking-tight leading-tight mb-3">
              Talent in Tech Spotlight —{" "}
              <span className="text-chrome">Luke Adams.</span>
            </h3>
            <p className="text-[14px] text-[#52555B] leading-relaxed mb-5">
              {"Live at DataCloud USA 2025 with Dean Perrine on #JSATV. Luke shares what inspired him to enter digital infrastructure, why curiosity is the most important skill for young professionals, and his vision for a more sustainable, human-centered industry."}
            </p>
            <p className="text-[13px] text-[#71747B] leading-relaxed mb-5 border-l-2 border-[#D2D5DA] pl-3">
              {"JSA TV — Tech & Telecom News spotlights breaking announcements and expert perspectives across cloud, AI, fiber, subsea, and network infrastructure."}
            </p>

            {/* Audience + meta strip */}
            <div className="grid grid-cols-2 gap-px bg-[#D2D5DA] border hairline mb-5">
              <a
                href="https://www.youtube.com/@JaymieScottoTV"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tit-jsa-channel-link"
                className="bg-white p-4 hover:bg-[#F4F5F7] transition-colors group block"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Eye size={11} className="text-[#0F1117]" />
                  <span className="font-mono-label text-[9px] text-[#71747B]">
                    Audience
                  </span>
                </div>
                <div className="font-display text-2xl font-extrabold text-[#0F1117] leading-none">
                  197K
                </div>
                <div className="text-[11px] text-[#52555B] mt-1 flex items-center gap-1">
                  @JaymieScottoTV subscribers
                  <ArrowUpRight size={10} className="text-[#71747B] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>
              <div className="bg-white p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <Mic size={11} className="text-[#0F1117]" />
                  <span className="font-mono-label text-[9px] text-[#71747B]">
                    Host
                  </span>
                </div>
                <div className="font-display text-[15px] font-bold text-[#0F1117] leading-tight">
                  Dean Perrine
                </div>
                <div className="text-[11px] text-[#52555B] mt-1">
                  JSA TV · Austin, TX
                </div>
              </div>
            </div>

            {/* Hashtags */}
            <div className="flex flex-wrap gap-1.5 mb-6">
              {HASHTAGS.map((h) => (
                <span
                  key={h}
                  className="chrome-tile px-2.5 py-1 font-mono-label text-[9px] text-[#0F1117] flex items-center gap-1"
                >
                  <Hash size={9} className="text-[#71747B]" />
                  {h}
                </span>
              ))}
            </div>

            <a
              href={JSA_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tit-watch-jsa-btn"
              className="chrome-btn-dark inline-flex w-fit items-center gap-2 font-mono-label text-[12px] px-6 py-4 mt-auto"
            >
              <Play size={14} />
              Watch on YouTube
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        {/* Stats + companies */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Stats */}
          <div className="lg:col-span-4 grid grid-cols-1 gap-px bg-[#D2D5DA] border hairline">
            {STATS.map(({ Icon, value, label }) => (
              <div
                key={label}
                className="bg-white p-5 flex items-center gap-4 hover:bg-[#F4F5F7] transition-colors"
              >
                <span className="chrome-tile w-12 h-12 flex items-center justify-center shrink-0">
                  <Icon size={18} className="text-[#0F1117]" />
                </span>
                <div>
                  <div className="font-display text-2xl font-extrabold text-[#0F1117] leading-none">
                    {value}
                  </div>
                  <div className="text-[12px] text-[#52555B] mt-1">{label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Companies */}
          <div className="lg:col-span-8 chrome-surface relative p-6 md:p-7">
            <CornerMarks color="#A9ADB4" />
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono-label text-[10px] text-[#71747B]">
                / Direct exposure to leaders at
              </span>
              <span className="font-mono-label text-[10px] text-[#0F1117]">
                7 companies
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[#D2D5DA] border hairline">
              {COMPANIES.map((c, i) => (
                <div
                  key={c}
                  data-testid={`tit-company-${i}`}
                  className="chrome-tile px-4 py-4 flex items-center justify-center text-center"
                >
                  <span className="font-display text-[15px] font-bold text-[#0F1117] tracking-tight">
                    {c}
                  </span>
                </div>
              ))}
              {/* Filler tile with DCYB wordmark */}
              <div className="hidden md:flex bg-white px-4 py-4 items-center justify-center gap-2">
                <span className="font-mono-label text-[14px] text-[#71747B] leading-none">
                  +
                </span>
                <img
                  src="/dcyb-wordmark.png"
                  alt="DCYB"
                  className="h-5 w-auto object-contain opacity-90"
                  draggable="false"
                />
              </div>
              {/* "Next: yours." — soft CTA tile linking to Get Involved */}
              <Link
                to="/get-involved"
                data-testid="next-yours-tile"
                aria-label="Your company — get involved"
                title="Next: yours"
                className="hidden md:flex group chrome-tile px-3 py-3 items-center justify-center transition-colors hover:bg-[#0F1117] relative overflow-hidden"
              >
                <svg
                  viewBox="0 0 64 64"
                  className="h-9 w-9 text-[#52555B] group-hover:text-white transition-colors"
                  fill="currentColor"
                  aria-hidden
                >
                  <path d="M32 6c-6.6 0-12 5.4-12 12s5.4 12 12 12 12-5.4 12-12S38.6 6 32 6zm0 28c-9.4 0-28 4.7-28 14v8h56v-8c0-9.3-18.6-14-28-14z" />
                </svg>
                {/* Tiny live dot in corner */}
                <span className="absolute top-2 right-2 inline-flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-[#22C55E] animate-ping opacity-70" />
                  <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* Closing CTA */}
        <div className="chrome-surface relative p-6 md:p-8 flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="font-mono-label text-[10px] text-[#71747B] mb-2">
              / Talent in Tech · DataCloud USA
            </div>
            <h4 className="font-display text-xl md:text-2xl font-bold text-[#0F1117] tracking-tight">
              Cultivating the people who will run the next era of compute.
            </h4>
          </div>
          <a
            href={PROGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tit-cta-btn"
            className="chrome-btn-dark inline-flex items-center gap-2 font-mono-label text-[12px] px-6 py-4"
          >
            About the program
            <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
